import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings } from "lucide-react";

const KEY = "fs_cookie_consent_v1";

const DEFAULT_PREFS = {
  necessary: true,    // Always on
  analytics: false,
  marketing: false,
};

function applyConsent(prefs) {
  if (typeof window === "undefined") return;

  // Update Google consent (Consent Mode v2)
  const grant = (b) => (b ? "granted" : "denied");
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: grant(prefs.marketing),
      ad_user_data: grant(prefs.marketing),
      ad_personalization: grant(prefs.marketing),
      analytics_storage: grant(prefs.analytics),
      functionality_storage: "granted",
      security_storage: "granted",
    });
  }
  // Push to dataLayer for GTM-side tag gating
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "cookie_consent_update",
    consent_analytics: prefs.analytics,
    consent_marketing: prefs.marketing,
  });

  // Load the trackers the visitor just granted (index.html exposes this).
  // Only fires categories that are allowed; essential/Consent-Mode-gated
  // Google tags load regardless but stay cookieless until granted.
  try {
    window.__fsConsent = { analytics: !!prefs.analytics, marketing: !!prefs.marketing };
    if (typeof window.__fsActivateTrackers === "function") {
      window.__fsActivateTrackers(window.__fsConsent);
    }
  } catch (e) { /* noop */ }

  // PostHog opt-in/out (loaded lazily once analytics is granted)
  try {
    if (window.posthog) {
      if (prefs.analytics) window.posthog.opt_in_capturing();
      else window.posthog.opt_out_capturing();
    }
  } catch (e) { /* noop */ }
}

function setDefaultDeniedConsent() {
  // Consent Mode default is now set inline in index.html <head> before any
  // tag loads (honouring a returning visitor's saved choice). Kept as a
  // no-op so existing call sites stay valid.
}

export default function CookieConsent() {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    setDefaultDeniedConsent();
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        setPrefs(stored);
        applyConsent(stored);
      } else if (typeof window !== "undefined" && (
        window.location.pathname.startsWith("/portal/") ||
        window.location.pathname.startsWith("/login") ||
        window.location.pathname.startsWith("/register") ||
        window.location.pathname.startsWith("/set-password") ||
        window.location.pathname.startsWith("/reset-password") ||
        window.location.pathname.startsWith("/forgot-password")
      )) {
        // Auth flows / authenticated portal, implicit accept (necessary+analytics)
        const implicit = { necessary: true, analytics: true, marketing: false };
        localStorage.setItem(KEY, JSON.stringify(implicit));
        applyConsent(implicit);
        setPrefs(implicit);
      } else {
        // first visit, show banner
        setTimeout(() => setOpen(true), 700);
      }
    } catch (e) {
      setOpen(true);
    }
    // Allow footer link to open the panel
    const handler = () => { setOpen(true); setShowSettings(true); };
    window.addEventListener("fs:open-cookie-prefs", handler);
    return () => window.removeEventListener("fs:open-cookie-prefs", handler);
  }, []);

  const save = (next) => {
    const final = { ...next, necessary: true };
    localStorage.setItem(KEY, JSON.stringify(final));
    applyConsent(final);
    setPrefs(final);
    setOpen(false);
    setShowSettings(false);
  };

  const acceptAll  = () => save({ necessary: true, analytics: true, marketing: true });
  const rejectAll  = () => save({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => save(prefs);

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] p-4 md:p-6 pointer-events-none" data-testid="cookie-banner">
      <div className="max-w-5xl mx-auto bg-white border-t-2 border-[#B8965A] shadow-2xl pointer-events-auto">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center p-6 md:p-7">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Cookie size={16} className="text-[#B8965A]"/>
              <span className="eyebrow text-[#B8965A]">Cookies & Privacy</span>
            </div>
            <h3 className="font-serif text-[#1C3E5F] text-xl md:text-2xl mb-2 leading-tight">
              We respect your privacy.
            </h3>
            <p className="text-[12.5px] md:text-sm text-[#1C3E5F]/75 leading-relaxed">
              Fortune Sourcings uses essential cookies to run the site. With your consent, we also use analytics and marketing cookies to understand how the site is used and to make our communications more relevant. You can change your preferences at any time. Read our{" "}
              <Link to="/legal/privacy" className="text-[#B8965A] underline hover:text-[#1C3E5F]">privacy policy</Link>{" "}and{" "}
              <Link to="/legal/cookies" className="text-[#B8965A] underline hover:text-[#1C3E5F]">cookie policy</Link>.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:flex-nowrap">
            <button
              data-testid="cookie-reject"
              onClick={rejectAll}
              className="btn-outline text-[#1C3E5F] border-[#1C3E5F] hover:bg-[#1C3E5F] hover:text-white !py-2.5 !px-4 !text-[11px] whitespace-nowrap"
            >
              Reject All
            </button>
            <button
              data-testid="cookie-settings"
              onClick={() => setShowSettings(true)}
              className="btn-outline text-[#1C3E5F] border-[#1C3E5F] hover:bg-[#1C3E5F] hover:text-white !py-2.5 !px-4 !text-[11px] whitespace-nowrap inline-flex items-center gap-1.5"
            >
              <Settings size={12}/> Customise
            </button>
            <button
              data-testid="cookie-accept"
              onClick={acceptAll}
              className="btn-primary !py-2.5 !px-5 !text-[11px] whitespace-nowrap"
            >
              Accept All
            </button>
          </div>
        </div>

        {showSettings && (
          <div className="border-t border-[#E2E8F0] p-6 md:p-7 bg-[#F8FAFC]" data-testid="cookie-settings-panel">
            <div className="flex items-start justify-between mb-5 gap-4">
              <div>
                <div className="font-serif text-[#1C3E5F] text-lg leading-tight mb-1">Cookie preferences</div>
                <div className="text-[12px] text-[#1C3E5F]/70">Choose which categories of cookies you allow. Necessary cookies cannot be disabled.</div>
              </div>
              <button onClick={() => setShowSettings(false)} className="text-[#1C3E5F]/60 hover:text-[#1C3E5F]" aria-label="Close settings"><X size={16}/></button>
            </div>
            <div className="space-y-2 mb-5">
              <PrefRow
                label="Strictly necessary"
                desc="Authentication, session, security and core site functionality. Always on."
                checked
                disabled
              />
              <PrefRow
                label="Analytics"
                desc="Helps us understand how visitors use the site (Google Analytics 4, Microsoft Clarity, PostHog), never used to identify you personally."
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                testId="pref-analytics"
              />
              <PrefRow
                label="Marketing"
                desc="Allows us to send relevant follow-up emails and measure/retarget campaigns via the Meta Pixel and LinkedIn Insight Tag."
                checked={prefs.marketing}
                onChange={(v) => setPrefs({ ...prefs, marketing: v })}
                testId="pref-marketing"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={rejectAll} className="btn-outline text-[#1C3E5F] border-[#1C3E5F] !py-2.5 !px-4 !text-[11px]" data-testid="cookie-reject-2">Reject All</button>
              <button onClick={saveCustom} className="btn-dark !py-2.5 !px-4 !text-[11px]" data-testid="cookie-save">Save Preferences</button>
              <button onClick={acceptAll} className="btn-primary !py-2.5 !px-4 !text-[11px]" data-testid="cookie-accept-2">Accept All</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrefRow({ label, desc, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-start gap-3 p-3 border ${disabled ? "border-[#E2E8F0] bg-white/50" : "border-[#E2E8F0] bg-white hover:border-[#B8965A]"} cursor-${disabled ? "default" : "pointer"} transition-colors`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        data-testid={testId}
        className="mt-1 accent-[#B8965A] w-4 h-4"
      />
      <div className="flex-1">
        <div className="text-[13px] font-semibold text-[#1C3E5F]">{label}{disabled && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#B8965A]">Always on</span>}</div>
        <div className="text-[11.5px] text-[#1C3E5F]/65 mt-0.5 leading-relaxed">{desc}</div>
      </div>
    </label>
  );
}
